import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Heart, Users, Award, Zap, Shield, TrendingUp } from 'lucide-react';

const About = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const values = [
    {
      icon: <Award />,
      title: 'Excellence',
      description: 'Nous visons l\'excellence dans chaque projet, en livrant des solutions de qualité supérieure.',
    },
    {
      icon: <Users />,
      title: 'Collaboration',
      description: 'Nous travaillons en étroite collaboration avec nos clients pour co-créer des solutions adaptées.',
    },
    {
      icon: <Zap />,
      title: 'Innovation',
      description: 'Nous restons à la pointe de la technologie pour apporter des solutions modernes et performantes.',
    },
    {
      icon: <Shield />,
      title: 'Confiance',
      description: 'Nous construisons des relations durables basées sur la transparence et l\'intégrité.',
    },
  ];

  const approach = [
    {
      step: '01',
      title: 'Écoute & Analyse',
      description: 'Nous commençons par comprendre vos besoins, vos objectifs et vos contraintes spécifiques.',
    },
    {
      step: '02',
      title: 'Conception & Stratégie',
      description: 'Nous élaborons une stratégie sur mesure et concevons une architecture technique adaptée.',
    },
    {
      step: '03',
      title: 'Développement Agile',
      description: 'Nous développons de manière itérative, en vous impliquant à chaque étape du processus.',
    },
    {
      step: '04',
      title: 'Livraison & Support',
      description: 'Nous livrons des solutions performantes et assurons un suivi continu pour garantir votre succès.',
    },
  ];

  const founders = [
    {
      name: 'Richard K.',
      role: 'Développeur Full-Stack & Fondateur',
      bio: 'Expert en développement web et mobile avec plus de 5 ans d\'expérience. Spécialisé dans les architectures modernes et les solutions cloud.',
      image: './images/homme5.png',
    },
    {
      name: 'Priscille M.',
      role: 'Directrice Stratégie & Data & Co-Fondatrice',
      bio: 'Spécialiste en stratégie digitale et analyse de données. Passionnée par la transformation des insights en actions concrètes.',
      image: './images/femme1.png',
    },
    {
      name: 'Philemon. W.',
      role: 'Développeur Back-End & Co-Fondateur',
      bio: 'Expert en architectures back-end robustes et scalables. Maîtrise des bases de données et des APIs haute performance.',
      image: './images/homme4.png',
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-cyan-50 to-blue-50 py-24 sm:py-32">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate="animate"
            variants={stagger}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 leading-tight"
            >
              Qui sommes-nous ?
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 leading-relaxed"
            >
              Kynovia Tech est une entreprise française spécialisée dans la transformation digitale, la data et le développement logiciel. Nous accompagnons les entreprises dans leur évolution technologique avec rigueur et créativité.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 gap-16 items-center"
          >
            <motion.div variants={fadeInUp}>
              <div className="inline-flex items-center space-x-2 bg-cyan-100 text-cyan-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Target size={16} />
                <span>Notre mission</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Transformer vos ambitions en réalité digitale
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Notre mission est d'accompagner les entreprises dans leur transformation numérique en créant des solutions technologiques innovantes, performantes et parfaitement adaptées à leurs besoins spécifiques.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Nous croyons que la technologie doit être un levier de croissance et d'efficacité, pas une contrainte. C'est pourquoi nous mettons notre expertise au service de votre réussite.
              </p>
            </motion.div>
            <motion.div
              variants={fadeInUp}
              className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl p-12 text-white"
            >
              <Eye size={48} className="mb-6" />
              <h3 className="text-2xl font-bold mb-4">Notre vision</h3>
              <p className="text-cyan-100 leading-relaxed text-lg">
                Devenir le partenaire technologique de référence pour les entreprises qui souhaitent innover et se transformer durablement, en alliant excellence technique et impact mesurable.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Nos valeurs
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-gray-600 max-w-3xl mx-auto">
              Les principes qui guident notre action au quotidien
            </motion.p>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -8 }}
                className="bg-white p-8 rounded-2xl border border-gray-200 hover:border-cyan-300 hover:shadow-2xl transition-all text-center group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white mx-auto mb-6 group-hover:scale-110 transition-transform">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Notre approche
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une méthodologie éprouvée pour garantir votre succès
            </motion.p>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {approach.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="relative"
              >
                <div className="bg-gradient-to-br from-white to-cyan-50 p-8 rounded-2xl border border-gray-200 hover:border-cyan-300 hover:shadow-2xl transition-all h-full">
                  <div className="text-6xl font-bold text-cyan-200 mb-4">{item.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
                {index < approach.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-cyan-300 to-transparent" />
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Les co-fondateurs
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une équipe passionnée et compétente à votre service
            </motion.p>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-3 gap-8"
          >
            {founders.map((founder, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -8 }}
                className="bg-white rounded-2xl border border-gray-200 hover:border-cyan-300 hover:shadow-2xl transition-all overflow-hidden group"
              >
                <div className="aspect-square overflow-hidden h-48">
                  <img
                    src={founder.image}
                    alt={founder.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{founder.name}</h3>
                  <p className="text-cyan-600 font-medium mb-4">{founder.role}</p>
                  <p className="text-gray-600 leading-relaxed">{founder.bio}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;